import { useEffect, useMemo, useRef, useState, memo } from 'react';
import { format, getDay, differenceInMinutes } from 'date-fns';
import { cn } from '@/lib/core/utils';
import { getEmployeeAvatarColor, getEmployeeInitials } from '@/lib/employees/avatar';
import {
  DEMO_WEEK_DAYS,
  DEMO_EMPLOYEES,
  DEMO_OPENING_HOURS,
  DEMO_EMPLOYEE_ROLES,
  INITIAL_DEMO_SHIFTS,
  NEW_DEMO_SHIFTS,
  ASSIGNED_DEMO_SHIFTS,
  MONDAY_PATTERN_DEMO_SHIFTS,
  ASSIGNED_MONDAY_PATTERN_DEMO_SHIFTS,
} from './demoShiftData';
import type { DashboardState } from './types';
import type { DayShift } from '@/types';
import { Clock, Users, Search, Info, UserX, CalendarDays, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';

type DemoActionsPanelMode = 'employees' | 'day-actions' | 'shifts';

// ── Constants ────────────────────────────────────────────────────────────

const DISPLAY_START_HOUR = 7;
const DISPLAY_END_HOUR = 21;
const TOTAL_HOURS = DISPLAY_END_HOUR - DISPLAY_START_HOUR; // 16
const INTERVALS_PER_HOUR = 4; // 15-min intervals
const TOTAL_INTERVALS = TOTAL_HOURS * INTERVALS_PER_HOUR; // 64
const DAY_LABEL_WIDTH = 32; // px — matches DAY_COLUMN_WIDTH_PX
const ROW_HEIGHT = 64; // px per shift row — matches ROW_HEIGHT_PX
const SHIFT_HEIGHT = 56; // px — ROW_HEIGHT - 8 (4px top + 4px bottom padding)
const SHIFT_TOP_OFFSET = 4; // px from top of row
const TIME_HEADER_HEIGHT = 40; // px
const DAY_GAP = 8; // px gap between days — matches GAP_HEIGHT_PX

const TIME_LABELS = Array.from({ length: TOTAL_HOURS }, (_, i) => {
  const hour = DISPLAY_START_HOUR + i;
  return `${hour.toString().padStart(2, '0')}:00`;
});

const NEW_SHIFT_IDS = ['new-1', 'new-2', 'new-3'];

// ── Helpers ──────────────────────────────────────────────────────────────

function getShiftHour(date: Date): number {
  return date.getHours() + date.getMinutes() / 60;
}

function getOpeningHoursForDay(dayOfWeek: number) {
  const entry = DEMO_OPENING_HOURS.find(oh => oh.day_of_week === dayOfWeek);
  if (!entry || !entry.is_open) return null;
  const [oh, om] = entry.opening_time.split(':').map(Number);
  const [ch, cm] = entry.closing_time.split(':').map(Number);
  return { open: oh + om / 60, close: ch + cm / 60 };
}

// Group shifts by day index
function groupShiftsByDay(shifts: DayShift[]): Map<number, DayShift[]> {
  const map = new Map<number, DayShift[]>();
  for (let i = 0; i < 7; i++) map.set(i, []);

  for (const shift of shifts) {
    const shiftDate = new Date(shift.startTime);
    for (let dayIdx = 0; dayIdx < DEMO_WEEK_DAYS.length; dayIdx++) {
      const weekDay = DEMO_WEEK_DAYS[dayIdx];
      if (
        shiftDate.getFullYear() === weekDay.getFullYear() &&
        shiftDate.getMonth() === weekDay.getMonth() &&
        shiftDate.getDate() === weekDay.getDate()
      ) {
        map.get(dayIdx)!.push(shift);
        break;
      }
    }
  }
  return map;
}

const getTextSize = (w: number): string => {
  if (w < 80) return 'text-3xs';
  if (w < 120) return 'text-3xs';
  return 'text-2xs';
};

// ── Component ────────────────────────────────────────────────────────────

interface DemoCalendarStaticProps {
  dashboard: DashboardState;
}

export const DemoCalendarStatic = memo(function DemoCalendarStatic({ dashboard }: DemoCalendarStaticProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(0);
  const [panelMode, setPanelMode] = useState<DemoActionsPanelMode>('employees');

  // Single ResizeObserver on the grid container
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setGridWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Compute shifts from dashboard state
  const shifts = useMemo(() => {
    const hasNewShifts = dashboard.shifts.some(s => s.id === 'new-1' || s.id === 'new-2');
    const hasMondayPattern = dashboard.shifts.some(s => s.id === 'new-3');
    const newShiftsAssigned = dashboard.shifts.some(s => (s.id === 'new-1' || s.id === 'new-2') && s.assigned);

    if (newShiftsAssigned && hasMondayPattern) {
      return [...INITIAL_DEMO_SHIFTS, ...ASSIGNED_MONDAY_PATTERN_DEMO_SHIFTS];
    } else if (newShiftsAssigned) {
      return [...INITIAL_DEMO_SHIFTS, ...ASSIGNED_DEMO_SHIFTS];
    } else if (hasMondayPattern) {
      return [...INITIAL_DEMO_SHIFTS, ...MONDAY_PATTERN_DEMO_SHIFTS];
    } else if (hasNewShifts) {
      return [...INITIAL_DEMO_SHIFTS, ...NEW_DEMO_SHIFTS];
    }
    return INITIAL_DEMO_SHIFTS;
  }, [dashboard.shifts]);

  const shiftsByDay = useMemo(() => groupShiftsByDay(shifts), [shifts]);

  // Width of the scrollable grid area (minus day label)
  const cellAreaWidth = Math.max(gridWidth - DAY_LABEL_WIDTH, 1);
  const intervalWidth = cellAreaWidth / TOTAL_INTERVALS;

  // Are the new shifts currently animating in?
  const NEW_SHIFT_IDS = ['new-1', 'new-2', 'new-3'];
  const isNewShiftAnimating = dashboard.shifts.some(s => NEW_SHIFT_IDS.includes(s.id) && s.animating);
  const isNewShiftAssigned = dashboard.shifts.some(s => NEW_SHIFT_IDS.includes(s.id) && s.assigned);

  return (
    <div
      data-calendar-container
      data-mode="idle"
      className="bg-card border rounded-lg flex flex-col h-full"
    >
      <div className="flex flex-col w-full overflow-hidden flex-1 px-2 pb-2">
        {/* Time header row */}
        <div className="flex w-full flex-nowrap">
          <div className="flex-1 min-w-0 overflow-hidden">
            <div
              className="grid w-full"
              style={{
                gridTemplateColumns: `${DAY_LABEL_WIDTH}px repeat(${TOTAL_HOURS}, 1fr)`,
                gridTemplateRows: `${TIME_HEADER_HEIGHT}px`,
              }}
            >
              {/* Day column header cell (empty) */}
              <div
                className="border-b border-border flex items-center justify-center"
                style={{ gridColumn: 1, gridRow: 1, minHeight: TIME_HEADER_HEIGHT }}
              />
              {/* Hour markers with rotated labels */}
              {TIME_LABELS.map((label, i) => (
                <div
                  key={i}
                  className="border-b border-l border-border text-xs text-center font-medium flex items-center justify-center bg-transparent"
                  style={{
                    gridColumn: i + 2,
                    gridRow: 1,
                    minHeight: TIME_HEADER_HEIGHT,
                    position: 'relative',
                  }}
                >
                  <span
                    className="text-muted-foreground"
                    style={{
                      position: 'absolute',
                      left: '4px',
                      top: '50%',
                      transform: 'translate(-50%, -50%) rotate(-90deg)',
                      whiteSpace: 'nowrap',
                      fontSize: '10px',
                    }}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {/* Actions header spacer */}
          <div className="hidden lg:block w-36 flex-shrink-0 border-b border-border" />
        </div>

        {/* Calendar + actions panel row */}
        <div className="flex w-full overflow-hidden flex-1">
          {/* Calendar grid */}
          <div ref={gridRef} className="flex-1 min-w-0 overflow-hidden relative flex flex-col">
            {DEMO_WEEK_DAYS.map((dayDate, dayIdx) => {
              const dayOfWeek = getDay(dayDate); // 0=Sun..6=Sat
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
              const dayShifts = shiftsByDay.get(dayIdx) || [];
              const maxRows = Math.max(dayShifts.length, 1);
              const openingHours = getOpeningHoursForDay(dayOfWeek);

              const isLastDay = dayIdx === DEMO_WEEK_DAYS.length - 1;

              return (
                <DayRow
                  key={dayIdx}
                  dayDate={dayDate}
                  isWeekend={isWeekend}
                  dayShifts={dayShifts}
                  maxRows={maxRows}
                  openingHours={openingHours}
                  intervalWidth={intervalWidth}
                  isNewShiftAnimating={isNewShiftAnimating}
                  isNewShiftAssigned={isNewShiftAssigned}
                  isLastDay={isLastDay}
                />
              );
            })}
          </div>

          {/* Actions panel */}
          <div className="hidden lg:flex w-36 max-w-36 flex-shrink-0 h-full flex-col bg-transparent border-l pointer-events-auto">
            {/* Fixed header */}
            <div className="p-2 space-y-2 flex-shrink-0 max-w-full">
              {/* Mode selector — real ButtonGroup */}
              <ButtonGroup className="w-full" orientation="vertical">
                <Button
                  variant={panelMode === 'employees' ? 'default' : 'outline'}
                  size="sm"
                  className="w-full h-7 text-xs justify-start"
                  onClick={() => setPanelMode('employees')}
                >
                  Employees
                </Button>
                <Button
                  variant={panelMode === 'day-actions' ? 'default' : 'outline'}
                  size="sm"
                  className="w-full h-7 text-xs justify-start"
                  onClick={() => setPanelMode('day-actions')}
                >
                  Day Actions
                </Button>
                <Button
                  variant={panelMode === 'shifts' ? 'default' : 'outline'}
                  size="sm"
                  className="w-full h-7 text-xs justify-start"
                  onClick={() => setPanelMode('shifts')}
                >
                  Shifts
                </Button>
              </ButtonGroup>

              {/* Search input — only in employees mode */}
              {panelMode === 'employees' && (
                <div className="relative flex items-center">
                  <div className="h-6 text-xs bg-background border rounded-md pl-2 pr-6 flex items-center text-muted-foreground/50 w-full">
                    Search...
                  </div>
                  <Search className="h-3 w-3 absolute right-2 text-muted-foreground pointer-events-none" />
                </div>
              )}

              {/* Role filter — only in employees mode */}
              {panelMode === 'employees' && (
                <Button
                  variant="outline"
                  className="h-8 w-full justify-start gap-2 text-xs"
                >
                  <Users className="h-3.5 w-3.5" />
                  Roles
                </Button>
              )}
            </div>

            {/* Scrollable content — changes based on mode */}
            <div className="flex-1 overflow-y-auto px-2 pb-2">
              {panelMode === 'employees' && (
                <div className="space-y-2">
                  {DEMO_EMPLOYEES.map(emp => (
                    <EmployeeCard key={emp.id} id={emp.id} name={emp.name} />
                  ))}
                </div>
              )}

              {panelMode === 'day-actions' && (
                <div className="space-y-2 py-1">
                  <p className="text-2xs text-muted-foreground px-1">Select a day to see available actions</p>
                  <div className="space-y-1">
                    {['Create shift', 'Clear all shifts', 'Apply template', 'Save as template'].map(action => (
                      <div key={action} className="flex items-center gap-2 p-1.5 border rounded bg-muted/20 text-xs text-muted-foreground">
                        <CalendarDays className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {panelMode === 'shifts' && (
                <div className="space-y-2 py-1">
                  <p className="text-2xs text-muted-foreground px-1 font-medium">Shift Templates</p>
                  <div className="space-y-1">
                    {['Morning (8-16)', 'Afternoon (12-20)', 'Full day (8-20)'].map(tmpl => (
                      <div key={tmpl} className="flex items-center gap-2 p-1.5 border rounded bg-muted/20 text-xs text-muted-foreground">
                        <Layers className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{tmpl}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-2xs text-muted-foreground px-1 font-medium pt-2">Day Templates</p>
                  <div className="space-y-1">
                    {['Weekday default', 'Weekend'].map(tmpl => (
                      <div key={tmpl} className="flex items-center gap-2 p-1.5 border rounded bg-muted/20 text-xs text-muted-foreground">
                        <CalendarDays className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{tmpl}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// ── Day Row ──────────────────────────────────────────────────────────────

interface DayRowProps {
  dayDate: Date;
  isWeekend: boolean;
  dayShifts: DayShift[];
  maxRows: number;
  openingHours: { open: number; close: number } | null;
  intervalWidth: number;
  isNewShiftAnimating: boolean;
  isNewShiftAssigned: boolean;
  isLastDay: boolean;
}

function DayRow_({
  dayDate,
  isWeekend,
  dayShifts,
  maxRows,
  openingHours,
  intervalWidth,
  isNewShiftAnimating,
  isNewShiftAssigned,
  isLastDay,
}: DayRowProps) {
  return (
    <div
      className="grid relative"
      style={{
        gridTemplateColumns: `${DAY_LABEL_WIDTH}px repeat(${TOTAL_INTERVALS}, ${intervalWidth}px)`,
        gridTemplateRows: `repeat(${maxRows}, ${ROW_HEIGHT}px)`,
        marginBottom: isLastDay ? 0 : DAY_GAP,
      }}
    >
      {/* Day label - spans all rows in column 1, with preparation status border */}
      <div
        className={cn(
          'flex flex-col items-center justify-center text-center select-none border-r border-t border-b border-border z-10',
          // Status border-left (preparation status)
          dayShifts.length > 0 && dayShifts.every(s => s.employeeId)
            ? 'border-l-2 border-l-green-500 dark:border-l-green-400'
            : dayShifts.length > 0
              ? 'border-l-2 border-l-orange-500 dark:border-l-orange-400'
              : 'border-l-2 border-l-red-500 dark:border-l-red-400',
          // Background color
          isWeekend
            ? 'bg-primary text-primary-foreground'
            : 'bg-background text-foreground'
        )}
        style={{ gridColumn: 1, gridRow: `1 / span ${maxRows}` }}
      >
        <span className="text-3xs leading-tight opacity-80">{format(dayDate, 'EEE')}</span>
        <span className="text-sm font-semibold leading-tight">{format(dayDate, 'd')}</span>
      </div>

      {/* Hour grid background — CSS backgrounds replace 500+ DOM cells */}
      <div
        className="absolute pointer-events-none"
        style={{
          left: DAY_LABEL_WIDTH,
          right: 0,
          top: 0,
          bottom: 0,
        }}
      >
        {/* Vertical hour lines — repeating CSS gradient, zero DOM cost */}
        <div
          className="absolute inset-y-0 left-0 right-0"
          style={{
            backgroundImage: `repeating-linear-gradient(to right, transparent 0, transparent ${INTERVALS_PER_HOUR * intervalWidth - 1}px, hsl(var(--border) / 0.06) 0, hsl(var(--border) / 0.06) 1px)`,
          }}
        />

        {/* Business hours highlight */}
        {openingHours && (
          <div
            className="absolute inset-y-0 bg-primary/10 dark:bg-primary/15"
            style={{
              left: (openingHours.open - DISPLAY_START_HOUR) * INTERVALS_PER_HOUR * intervalWidth,
              width: (openingHours.close - openingHours.open) * INTERVALS_PER_HOUR * intervalWidth,
              borderLeft: '2px solid hsl(var(--primary) / 0.3)',
              borderRight: '2px solid hsl(var(--primary) / 0.3)',
            }}
          />
        )}

        {/* Row separators */}
        {Array.from({ length: maxRows }, (_, i) => (
          <div
            key={i}
            className="absolute left-0 right-0"
            style={{
              top: i === 0 ? 0 : i * ROW_HEIGHT,
              height: 1,
              background: i === 0 || i === maxRows
                ? 'hsl(var(--border) / 0.20)'
                : 'hsl(var(--border) / 0.10)',
            }}
          />
        ))}
      </div>

      {/* Shift blocks - absolutely positioned over the grid */}
      {dayShifts.map((shift, rowIndex) => {
        const startHour = getShiftHour(new Date(shift.startTime));
        const endHour = getShiftHour(new Date(shift.endTime));
        const duration = endHour - startHour;

        const left = DAY_LABEL_WIDTH + (startHour - DISPLAY_START_HOUR) * INTERVALS_PER_HOUR * intervalWidth;
        const width = duration * INTERVALS_PER_HOUR * intervalWidth;
        const top = rowIndex * ROW_HEIGHT + SHIFT_TOP_OFFSET;

        const isUnassigned = !shift.employeeId;
        const isNew = NEW_SHIFT_IDS.includes(shift.id);
        const isAnimating = isNew && isNewShiftAnimating;
        const isAssigned = isNew && isNewShiftAssigned;
        const durationMinutes = differenceInMinutes(shift.endTime, shift.startTime);

        return (
          <div
            key={shift.id}
            className={cn(
              'shift-card h-[56px] rounded-md overflow-hidden',
              isUnassigned
                ? 'bg-orange-500/60 dark:bg-orange-400/60'
                : 'bg-blue-500/60 dark:bg-blue-400/60',
              isNew && isAnimating && 'ai-demo-shift-appear',
              isNew && isAssigned && !isAnimating && 'ai-demo-shift-assign'
            )}
            style={{
              position: 'absolute',
              left,
              width: Math.max(width, 0),
              top,
              height: SHIFT_HEIGHT,
            }}
          >
            {width >= 25 && (
              <div className="flex items-center h-full w-full pl-4 pr-1 overflow-hidden">
                <div className="flex flex-row items-stretch justify-between w-full h-full overflow-hidden gap-1">
                  {/* Left side: Title, duration, time */}
                  {width >= 100 && (
                    <div
                      className={cn(
                        'flex flex-col justify-between h-full py-2 text-white min-w-0 flex-1',
                        width < 120 ? 'text-3xs' : width < 150 ? 'text-2xs' : 'text-xs'
                      )}
                    >
                      <span className="block truncate font-medium leading-none">
                        {shift.title || 'Untitled'}
                      </span>
                      <span className={cn('block leading-none', width < 120 ? 'text-3xs' : width < 150 ? 'text-3xs' : 'text-3xs')}>
                        <span className="block truncate leading-none pb-1">
                          {Math.floor(durationMinutes / 60)}h{durationMinutes % 60 > 0 ? ` ${durationMinutes % 60}m` : ''}
                        </span>
                        <span className="block truncate leading-none">
                          {format(shift.startTime, 'HH:mm')}-{format(shift.endTime, 'HH:mm')}
                        </span>
                      </span>
                    </div>
                  )}

                  {/* Right side: Status badge + Employee name */}
                  <div className={cn(
                    'flex flex-col items-stretch flex-shrink-0 py-1',
                    width < 60 ? 'gap-0 w-6' : width < 80 ? 'gap-0.5 w-12' : width < 100 ? 'gap-0.5 w-16' : width < 150 ? 'gap-0.5 w-20' : 'gap-0.5 w-24'
                  )}>
                    {/* Status Badge */}
                    {width >= 60 && (
                      <div className="flex items-center gap-0.5 text-white/90 text-3xs px-1 h-5">
                        {isUnassigned ? (
                          <UserX className="h-3 w-3 flex-shrink-0" />
                        ) : (
                          <Clock className="h-3 w-3 flex-shrink-0" />
                        )}
                        {width >= 150 && (
                          <span className="font-medium truncate">
                            {isUnassigned ? 'Unassigned' : 'Pending'}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Employee indicator */}
                    {width >= 40 && (
                      width < 60 ? (
                        shift.employeeId ? (
                          <div className={cn(
                            'flex items-center justify-center w-full h-6 text-white font-bold rounded text-3xs',
                            getEmployeeAvatarColor(shift.employeeName || '')
                          )}>
                            {getEmployeeInitials(shift.employeeName || '')[0]}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center w-full h-6 bg-muted rounded">
                            <UserX className="h-3 w-3 text-muted-foreground" />
                          </div>
                        )
                      ) : (
                        <div className={cn(
                          'flex items-center justify-start w-full h-6 px-1.5 rounded truncate font-medium border',
                          'bg-card text-foreground border-muted-foreground/30',
                          getTextSize(width)
                        )}>
                          {shift.employeeName || 'Unassigned'}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const DayRow = memo(DayRow_);

// ── Employee Card ────────────────────────────────────────────────────────

function EmployeeCard({ id, name }: { id: string; name: string }) {
  const initials = getEmployeeInitials(name);
  const avatarColor = getEmployeeAvatarColor(name);
  const role = DEMO_EMPLOYEE_ROLES[id] || 'No Role';

  return (
    <div className="p-2 border rounded bg-muted/20 cursor-grab">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          {/* Avatar */}
          <div className={cn('h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold text-xs', avatarColor)}>
            {initials}
          </div>
          <div className="flex flex-col min-w-0 flex-1">
            <span className="font-medium text-xs truncate">{name}</span>
            <div className="flex items-center gap-1">
              <span className="text-2xs h-4 px-1 py-0 leading-none w-fit border rounded-md inline-flex items-center text-muted-foreground">
                {role}
              </span>
            </div>
          </div>
          {/* Info button */}
          <Button className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-muted/50 flex-shrink-0 text-muted-foreground">
            <Info className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
